import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataSeries } from '@next/core-lib/models';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
  UpsertBreederFlockRequest,
  UpsertBreederWeekRegistrationRequest,
} from './breeder-flock-service.model';
import {
  AnimalCountCardResponse,
  GetDataSeriesResponse,
  GetFarmResourcesResponse,
  GetFlocksResponse,
  GetHatcheriesResponse,
  GetRearingOrganizationsResponse,
  UpsertFarmResourceResponse,
  UpsertFlockResponse,
} from './flock-service.model';
import { LayerBackendService } from '../../shared/services/layer-backend.service';
import {
  AnimalCountCard,
  BreederFlock,
  BreederGraphDataResponse,
  BreederWeekDataResponse,
  BreederWeekRegistration,
  CarbonFootprintResponse,
  FLOCK_VARIANT,
  FarmResource,
  FarmResourcesDivision,
  Flock,
  FlockVariant,
  FlockWeekDataSet,
  HenHatchery,
  LayerFlock,
  PorphyrioCategories,
  RearingOrganization,
  WeekRegistration,
} from '../models/flock.model';

interface PoultryWeekRegistration extends Omit<WeekRegistration, 'weekNr'> {
  weekNumber: number;
}

interface PoultryWeekDataRowResponse {
  weekNumber: number;
  weekStartDate: string;
  weekEndDate: string;
  values?: Array<{
    parameter: string;
    value: number | null;
    unit?: string;
    numberOfDecimals?: number;
  }>;
  isInMoult: boolean;
  remarks?: string[];
}

interface PoultryWeekDataSeriesResponse {
  metaData?: {
    flockId?: number;
    flockName?: string;
    breedName?: string;
    marketingStandard?: string | number;
  };
  series?: Array<{
    parameterCode: string;
    values?: Array<{
      value: number | null;
      weekNumber?: number;
      dimensionValue?: number;
      properties?: Record<string, unknown>;
    }>;
  }>;
}

interface ParsedBenchmarkValue {
  parsedValue?: number | null;
}

const BREEDER_REGISTRATION_UNITS: Record<string, string> = {
  henWeight: 'g',
  roosterWeight: 'g',
  henFeedIntakeInGrams: 'g',
  roosterFeedIntakeInGrams: 'g',
  henWaterIntakeInLiters: 'l',
  roosterWaterIntakeInLiters: 'l',
  eggWeightInGrams: 'g',
  fertilityPercentage: '%',
  hatchPercentage: '%',
  lightHours: 'h',
  lightIntensity: 'lux',
  totalFeedPerWeek: 'kg',
  waterInLiters: 'l',
};

type BenchmarkValue = number | ParsedBenchmarkValue | null;

interface BenchmarkSeriesValue {
  value: BenchmarkValue;
  dimensionValue: number;
  properties?: Record<string, unknown> | null;
}

interface BenchmarkSeriesItem {
  parameterCode?: string;
  start?: number;
  end?: number;
  values?: BenchmarkSeriesValue[];
}

@Injectable({
  providedIn: 'root',
})
export class FlockService {
  constructor(
    protected http: HttpClient,
    private readonly backend: LayerBackendService,
  ) {}

  getFlocks(): Observable<Flock[] | HttpErrorResponse>;
  getFlocks(variant: typeof FLOCK_VARIANT.Layer): Observable<LayerFlock[] | HttpErrorResponse>;
  getFlocks(variant: typeof FLOCK_VARIANT.Breeder): Observable<BreederFlock[] | HttpErrorResponse>;
  getFlocks(variant: FlockVariant): Observable<Flock[] | HttpErrorResponse>;
  getFlocks(variant: FlockVariant = FLOCK_VARIANT.Layer): Observable<Flock[] | HttpErrorResponse> {
    return this.http
      .get<GetFlocksResponse<Flock> | Flock[]>(this.backend.getFlockBaseUrl(variant))
      .pipe(
        map((response) =>
          this.mapFlocks(Array.isArray(response) ? response : (response.flocks ?? [])),
        ),
        catchError((error: HttpErrorResponse) => this.handleArray404<Flock>(error)),
      );
  }

  getFlocksForAllLocations(): Observable<Flock[] | HttpErrorResponse>;
  getFlocksForAllLocations(
    variant: typeof FLOCK_VARIANT.Layer,
  ): Observable<LayerFlock[] | HttpErrorResponse>;
  getFlocksForAllLocations(
    variant: typeof FLOCK_VARIANT.Breeder,
  ): Observable<BreederFlock[] | HttpErrorResponse>;
  getFlocksForAllLocations(variant: FlockVariant): Observable<Flock[] | HttpErrorResponse>;
  getFlocksForAllLocations(
    variant: FlockVariant = FLOCK_VARIANT.Layer,
  ): Observable<Flock[] | HttpErrorResponse> {
    return this.http
      .get<
        GetFlocksResponse<Flock> | Flock[]
      >(`${this.backend.getFlockBaseUrl(variant)}/allLocations`)
      .pipe(
        map((response) =>
          this.mapFlocks(Array.isArray(response) ? response : (response.flocks ?? [])),
        ),
        catchError((error: HttpErrorResponse) => this.handleArray404<Flock>(error)),
      );
  }

  upsertFlock(
    flock: Flock,
    variant: FlockVariant,
  ): Observable<UpsertFlockResponse | HttpErrorResponse>;
  upsertFlock(
    flock: LayerFlock,
    variant: typeof FLOCK_VARIANT.Layer,
  ): Observable<UpsertFlockResponse | HttpErrorResponse>;
  upsertFlock(
    flock: BreederFlock,
    variant: typeof FLOCK_VARIANT.Breeder,
  ): Observable<UpsertFlockResponse | HttpErrorResponse>;
  upsertFlock(
    flock: Flock,
    variant: FlockVariant = FLOCK_VARIANT.Layer,
  ): Observable<UpsertFlockResponse | HttpErrorResponse> {
    let request: unknown = flock;
    if (variant === FLOCK_VARIANT.Breeder) {
      request = this.mapBreederUpsertRequest(flock);
    } else if (this.backend.isPoultryEnabled) {
      request = this.mapPoultryLayerUpsertRequest(flock);
    }

    return this.http
      .post<UpsertFlockResponse>(this.backend.getFlockBaseUrl(variant), request, {})
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => new Error(error.message))));
  }

  deleteFlock(id: number, variant: FlockVariant = FLOCK_VARIANT.Layer) {
    return this.http.delete(`${this.backend.getFlockBaseUrl(variant)}/${id}`);
  }

  getFlockById(id: number): Observable<Flock | HttpErrorResponse>;
  getFlockById(
    id: number,
    variant: typeof FLOCK_VARIANT.Layer,
  ): Observable<LayerFlock | HttpErrorResponse>;
  getFlockById(
    id: number,
    variant: typeof FLOCK_VARIANT.Breeder,
  ): Observable<BreederFlock | HttpErrorResponse>;
  getFlockById(id: number, variant: FlockVariant): Observable<Flock | HttpErrorResponse>;
  getFlockById(
    id: number,
    variant: FlockVariant = FLOCK_VARIANT.Layer,
  ): Observable<Flock | HttpErrorResponse> {
    return this.http.get<Flock>(`${this.backend.getFlockBaseUrl(variant)}/${id}`).pipe(
      map((flock) => (this.backend.isPoultryEnabled ? this.mapPoultryFlock(flock) : flock)),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(<Flock>{});
        }

        return throwError(() => new Error(error.message));
      }),
    );
  }

  public getHatcheries(
    variant: FlockVariant = FLOCK_VARIANT.Layer,
  ): Observable<HenHatchery[] | HttpErrorResponse> {
    return this.http
      .get<GetHatcheriesResponse | HenHatchery[]>(this.backend.getHatcheryUrl(variant))
      .pipe(
        map((response) => (Array.isArray(response) ? response : (response.hatcheries ?? []))),
        catchError((error: HttpErrorResponse) => this.handleArray404<HenHatchery>(error)),
      );
  }

  public getRearingOrganizations(): Observable<RearingOrganization[] | HttpErrorResponse> {
    return this.http
      .get<
        GetRearingOrganizationsResponse | RearingOrganization[]
      >(this.backend.getRearingOrganizationsUrl())
      .pipe(
        map((response) =>
          Array.isArray(response) ? response : (response.rearingOrganizations ?? []),
        ),
        catchError((error: HttpErrorResponse) => this.handleArray404<RearingOrganization>(error)),
      );
  }

  public getBreedersReferenceBenchmarks(
    kpi: string,
    eggMarketingStandard: number,
    breed: string,
  ): Observable<DataSeries<number, number> | HttpErrorResponse> {
    return this.http
      .get<GetDataSeriesResponse>(this.backend.benchmarkBaseUrl, {
        params: {
          parameter: kpi,
          eggMarketingStandard: eggMarketingStandard.toString(),
          breed,
        },
      })
      .pipe(
        map((response) => {
          const data = response.data as unknown as {
            metaData?: {
              parameters?: unknown[];
            };
            series?: BenchmarkSeriesItem[] | Record<string, BenchmarkSeriesItem[]>;
          };
          const parameters = (data.metaData?.parameters ?? [
            {
              code: kpi,
              name: kpi,
            },
          ]) as Array<{
            code: string;
            name: string;
          }>;

          if (Array.isArray(data.series)) {
            return {
              metaData: {
                parameters,
              },
              series: {
                benchmark: data.series.map((series) => ({
                  parameterCode: series.parameterCode ?? kpi,
                  ...series,
                })),
              },
            } as DataSeries<number, number>;
          }

          return {
            metaData: {
              parameters,
            },
            series: Object.fromEntries(
              Object.entries(data.series ?? {}).map(([key, series]) => [
                key,
                series.map((serie) => this.mapBenchmarkSeriesItem(serie, key)),
              ]),
            ),
          } as DataSeries<number, number>;
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(<DataSeries<number, number>>{});
          }

          return throwError(() => new Error(error.message));
        }),
      );
  }

  public getPorphyrioBenchmarks(
    kpi: string,
    breedCategory: string,
    eggMarketingStandard: number,
    breed: string,
  ): Observable<DataSeries<number, number> | HttpErrorResponse> {
    return this.http
      .get<GetDataSeriesResponse>(`${this.backend.porphyrioBenchmarkBaseUrl}/dataseries`, {
        params: {
          parameter: kpi,
          breedCategory,
          eggMarketingStandard: eggMarketingStandard.toString(),
          breed,
        },
      })
      .pipe(
        map((response) => {
          const data = response.data as unknown as {
            metaData?: {
              parameters?: unknown[];
              other?: Record<string, unknown>;
              properties?: Record<string, unknown>;
            };
            series?: Record<
              string,
              | BenchmarkSeriesItem[]
              | {
                  code?: string;
                  items?: Array<{
                    value: number | null;
                    key: number;
                    properties?: Record<string, unknown>;
                  }>;
                }
            >;
          };

          return {
            metaData: {
              parameters: (data.metaData?.parameters ?? []) as Array<{
                code: string;
                name: string;
              }>,
              other: data.metaData?.other ?? data.metaData?.properties ?? {},
            },
            series: Object.fromEntries(
              Object.entries(data.series ?? {}).map(([key, series]) => {
                if (Array.isArray(series)) {
                  return [key, series.map((serie) => this.mapBenchmarkSeriesItem(serie, key))];
                }

                return [
                  key,
                  [
                    {
                      parameterCode: series.code ?? key,
                      values: (series.items ?? []).map((item) => ({
                        value: item.value,
                        dimensionValue: item.key,
                        properties: item.properties,
                      })),
                    },
                  ],
                ];
              }),
            ),
          } as DataSeries<number, number>;
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(<DataSeries<number, number>>{});
          }

          return throwError(() => new Error(error.message));
        }),
      );
  }

  public getPorphyrioCategories(): Observable<PorphyrioCategories | HttpErrorResponse> {
    return this.http
      .get<PorphyrioCategories>(`${this.backend.porphyrioBenchmarkBaseUrl}/categories`)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(<PorphyrioCategories>{});
          }

          return throwError(() => new Error(error.message));
        }),
      );
  }

  getFlockWeekDataByFlockId(
    flockId: number,
    variant: FlockVariant = FLOCK_VARIANT.Layer,
  ): Observable<FlockWeekDataSet | HttpErrorResponse> {
    if (!this.backend.isPoultryEnabled) {
      return this.http
        .post<FlockWeekDataSet>(`${this.backend.flockWeekDataBaseUrl}/getFlockWeekDataByFlockId`, {
          flockId,
          inMemory: false,
        })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === 404) {
              return of(<FlockWeekDataSet>undefined);
            }

            return throwError(() => new Error(error.message));
          }),
        );
    }

    const baseUrl = this.backend.getFlockWeekDataBaseUrl(variant);
    if (variant === FLOCK_VARIANT.Breeder) {
      return forkJoin({
        rows: this.http.get<{ data: BreederWeekDataResponse[] }>(`${baseUrl}/${flockId}/week-data`),
        graph: this.http.get<BreederGraphDataResponse>(
          `${baseUrl}/${flockId}/week-data/graph-data`,
        ),
      }).pipe(
        map(({ rows, graph }) => this.mapBreederWeekDataResponse(flockId, rows.data ?? [], graph)),
        catchError((error: HttpErrorResponse) =>
          error.status === 404
            ? of(<FlockWeekDataSet>undefined)
            : throwError(() => new Error(error.message)),
        ),
      );
    }
    return forkJoin({
      rows: this.http.get<{ data: PoultryWeekDataRowResponse[] }>(
        `${baseUrl}/${flockId}/week-data`,
      ),
      dataSeries: this.http.get<PoultryWeekDataSeriesResponse>(
        `${baseUrl}/${flockId}/week-data/dataseries`,
      ),
    }).pipe(
      map(({ rows, dataSeries }) =>
        this.mapPoultryWeekDataResponse(flockId, rows.data ?? [], dataSeries),
      ),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(<FlockWeekDataSet>undefined);
        }

        return throwError(() => new Error(error.message));
      }),
    );
  }

  deleteFlockWeekDataByFlockIdAdnWeekNumber(
    flockId: number,
    weekNumber: number,
    variant: FlockVariant = FLOCK_VARIANT.Layer,
  ) {
    if (this.backend.isPoultryEnabled) {
      return this.http.delete(
        `${this.backend.getFlockBaseUrl(variant)}/${flockId}/${variant === FLOCK_VARIANT.Breeder ? 'weeks' : 'week-registrations'}/${weekNumber}`,
      );
    }

    return this.http.delete(
      `${this.backend.flockBaseUrl}/flockId/${flockId}/weekNumber/${weekNumber}`,
    );
  }

  upsertBreederWeekRegistration(
    flockId: number,
    weekRegistration: BreederWeekRegistration,
  ): Observable<UpsertFlockResponse | HttpErrorResponse> {
    const { weekNr, ...request } = weekRegistration;

    return this.http
      .put<UpsertFlockResponse>(
        `${this.backend.getFlockBaseUrl(FLOCK_VARIANT.Breeder)}/${flockId}/weeks/${weekNr}`,
        request as UpsertBreederWeekRegistrationRequest,
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => new Error(error.message))));
  }

  loadAnimalCountCards(
    flockId: number,
    variant: FlockVariant = FLOCK_VARIANT.Layer,
  ): Observable<AnimalCountCard[] | HttpErrorResponse> {
    const url = this.backend.isPoultryEnabled
      ? `${this.backend.getAnimalCountBaseUrl(variant)}/${flockId}/animal-count-card`
      : `${this.backend.animalCountBaseUrl}/?flockid=${flockId}`;

    return this.http.get<AnimalCountCardResponse>(url).pipe(
      map((response) => response.cards),
      catchError((error: HttpErrorResponse) => this.handleArray404<AnimalCountCard>(error)),
    );
  }

  getFarmResources(): Observable<
    { farmResources: FarmResource[]; flockResources: FarmResourcesDivision[] } | HttpErrorResponse
  > {
    return this.http
      .get<GetFarmResourcesResponse>(`${this.backend.sustainabilityBaseUrl}/farm-resources`)
      .pipe(
        map((response) => ({
          farmResources: response.farmResources,
          flockResources: response.flockResources,
        })),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of({ farmResources: [], flockResources: [] });
          }

          return throwError(() => new Error(error.message));
        }),
      );
  }

  getCarbonFootprint(flockId: number): Observable<CarbonFootprintResponse | HttpErrorResponse> {
    return this.http
      .get<CarbonFootprintResponse>(
        `${this.backend.sustainabilityBaseUrl}/carbon-footprint?flockid=${flockId}`,
      )
      .pipe(
        map((response) => response),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(<CarbonFootprintResponse>undefined);
          }

          return throwError(() => new Error(error.message));
        }),
      );
  }

  upsertFarmResource(
    farmResource: FarmResource,
  ): Observable<UpsertFarmResourceResponse | HttpErrorResponse> {
    return this.http
      .post<UpsertFarmResourceResponse>(
        `${this.backend.sustainabilityBaseUrl}/farm-resource`,
        farmResource,
        {},
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => new Error(error.message))));
  }

  deleteFarmResource(id: number) {
    return this.http.delete(`${this.backend.sustainabilityBaseUrl}/farm-resource/${id}`);
  }

  calculateCarbonFootprint(flockId: number): Observable<void | HttpErrorResponse> {
    return this.http
      .post<void>(`${this.backend.sustainabilityBaseUrl}/carbon-footprint?flockId=${flockId}`, null)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  refreshCarbonFootprint(flockId: number): Observable<void | HttpErrorResponse> {
    return this.http
      .post<void>(
        `${this.backend.sustainabilityBaseUrl}/refresh-carbon-footprint?flockid=${flockId}`,
        null,
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  private handleArray404<T>(error: HttpErrorResponse): Observable<T[]> {
    if (error.status === 404) {
      return of(<T[]>[]);
    }

    return throwError(() => new Error(error.message));
  }

  private mapFlocks(flocks: Flock[]): Flock[] {
    return this.backend.isPoultryEnabled
      ? flocks.map((flock) => this.mapPoultryFlock(flock))
      : flocks;
  }

  private mapPoultryFlock(flock: Flock): Flock {
    if (!flock.weekRegistrations) {
      return flock;
    }

    return {
      ...flock,
      weekRegistrations: flock.weekRegistrations.map((week) => {
        const { weekNumber, ...registration } = week as unknown as PoultryWeekRegistration;
        return { ...registration, weekNr: weekNumber };
      }),
    };
  }

  private mapBenchmarkSeriesItem(serie: BenchmarkSeriesItem, fallbackParameterCode: string) {
    return {
      ...serie,
      parameterCode: serie.parameterCode ?? fallbackParameterCode,
      values: (serie.values ?? []).map((item) => this.mapBenchmarkSeriesValue(item)),
    };
  }

  private mapBenchmarkSeriesValue(item: BenchmarkSeriesValue) {
    return {
      value: this.parseBenchmarkValue(item.value),
      dimensionValue: item.dimensionValue,
      properties: item.properties,
    };
  }

  private parseBenchmarkValue(value: BenchmarkValue): number | null {
    return typeof value === 'object' ? (value?.parsedValue ?? null) : value;
  }

  private mapBreederUpsertRequest(flock: Flock): UpsertBreederFlockRequest {
    const breederFlock = flock as BreederFlock;
    const { arrival, ...rest } = breederFlock;
    const { rearingOrganization, ...arrivalRest } = arrival;

    return {
      ...(Object.prototype.hasOwnProperty.call(breederFlock, 'id') ? { id: breederFlock.id } : {}),
      ...rest,
      weekRegistrations: this.mapPoultryWeekRegistrations(breederFlock.weekRegistrations ?? []),
      arrival: {
        ...arrivalRest,
        barnId: arrival.barn?.id,
        breedId: arrival.breed?.id,
        hatcheryId: arrival.hatchery?.id,
        rearingOrganizationId: rearingOrganization?.id ?? null,
        feedSystemHens: this.mapLookupName(arrival.feedSystemHens),
        feedSystemRoosters: this.mapLookupName(arrival.feedSystemRoosters),
        drinkSystem: this.mapLookupName(arrival.drinkSystem),
        nest: this.mapLookupName(arrival.nest),
      },
    };
  }

  private mapPoultryLayerUpsertRequest(flock: Flock) {
    return {
      ...flock,
      ...(flock.weekRegistrations && {
        weekRegistrations: this.mapPoultryWeekRegistrations(flock.weekRegistrations),
      }),
    };
  }

  private mapPoultryWeekRegistrations<T extends WeekRegistration>(registrations: T[]) {
    return registrations.map(({ weekNr, ...registration }) => ({
      ...registration,
      weekNumber: weekNr,
    }));
  }

  private mapLookupName<T extends string>(
    value: T | { name: T } | null | undefined,
  ): T | null | undefined {
    if (value == null) {
      return value === null ? null : undefined;
    }

    if (typeof value === 'string') {
      return value as T;
    }

    return (value as { name: T }).name;
  }

  private mapPoultryWeekDataResponse(
    flockId: number,
    rows: PoultryWeekDataRowResponse[],
    dataSeries: PoultryWeekDataSeriesResponse,
  ): FlockWeekDataSet {
    const parameters = new Map<string, { unit?: string; numberOfDecimals?: number }>();
    const mappedFlockId = dataSeries.metaData?.flockId ?? flockId;

    const data = rows.map((row) => ({
      calculations: Object.fromEntries(
        (row.values ?? []).map((value) => {
          parameters.set(value.parameter, {
            unit: value.unit,
            numberOfDecimals: value.numberOfDecimals,
          });
          return [
            value.parameter,
            {
              parameter: value.parameter,
              value: value.value,
              dataType: 'Double',
            },
          ];
        }),
      ),
      info: {
        flockId: mappedFlockId,
        flockName: dataSeries.metaData?.flockName,
        breedName: dataSeries.metaData?.breedName,
        marketingStandard: this.mapMarketingStandard(dataSeries.metaData?.marketingStandard),
      },
      isInMoult: row.isInMoult,
      remarks: row.remarks ?? [],
      values: row.values ?? [],
      week: row.weekNumber,
      weekStartDate: new Date(row.weekStartDate),
      weekEndDate: new Date(row.weekEndDate),
    }));

    const mappedSeries = (dataSeries.series ?? []).map((series) => ({
      parameterCode: series.parameterCode,
      start: this.getFirstDimensionValue(series.values),
      end: this.getLastDimensionValue(series.values),
      values: (series.values ?? []).map((value) => ({
        value: value.value,
        dimensionValue: value.dimensionValue ?? value.weekNumber,
        properties: value.properties,
      })),
    }));

    return {
      data,
      dataSeries: {
        metaData: {
          parameters: mappedSeries.map((series) => ({
            code: series.parameterCode,
            name: series.parameterCode,
            unit: parameters.get(series.parameterCode)?.unit,
            numberOfDecimals: parameters.get(series.parameterCode)?.numberOfDecimals ?? 0,
            aggregationType: 'Last',
            dataType: 'Double',
          })),
        },
        series: {
          [mappedFlockId]: mappedSeries,
        },
      },
    } as FlockWeekDataSet;
  }

  private mapBreederWeekDataResponse(
    flockId: number,
    rows: BreederWeekDataResponse[],
    graph: BreederGraphDataResponse,
  ): FlockWeekDataSet {
    const mappedFlockId = graph.metaData?.flockId ?? flockId;
    const data = rows.map((row) => {
      const values = [
        ...Object.entries(row.flockState ?? {}).map(([parameter, value]) => ({
          parameter: parameter[0].toUpperCase() + parameter.slice(1),
          value,
        })),
        ...Object.entries(row.registrations ?? {}).map(([parameter, value]) => ({
          parameter: parameter[0].toUpperCase() + parameter.slice(1),
          value: value ?? null,
          unit: BREEDER_REGISTRATION_UNITS[parameter] ?? '',
        })),
        ...(row.kpis ?? []).map((kpi) => ({
          parameter: kpi.code,
          value: kpi.value,
          unit: kpi.unit,
        })),
      ];
      return {
        calculations: Object.fromEntries(
          values.map((value) => [
            value.parameter,
            {
              parameter: value.parameter,
              value: value.value,
              dataType: 'Double',
            },
          ]),
        ),
        info: {
          flockId: mappedFlockId,
          flockName: graph.metaData?.flockName,
          breedName: graph.metaData?.breedName,
        },
        isInMoult: false,
        remarks: [] as string[],
        values,
        week: row.weekNumber,
        weekStartDate: new Date(row.weekStartDate),
        weekEndDate: new Date(row.weekEndDate),
      };
    });
    const series = (graph.series ?? []).map((item) => ({
      parameterCode: item.code,
      start: this.getFirstDimensionValue(item.points),
      end: this.getLastDimensionValue(item.points),
      values: (item.points ?? []).map((point) => ({
        value: point.value,
        dimensionValue: point.weekNumber,
      })),
    }));
    return {
      data,
      dataSeries: {
        metaData: {
          parameters: (graph.series ?? []).map((item) => ({
            code: item.code,
            name: item.code,
            unit: item.unit,
            numberOfDecimals: item.decimals,
            aggregationType: 'Last',
            dataType: 'Double',
          })),
        },
        series: { [mappedFlockId]: series },
      },
    } as FlockWeekDataSet;
  }

  private mapMarketingStandard(marketingStandard?: string | number): number | undefined {
    if (typeof marketingStandard === 'number') {
      return marketingStandard;
    }

    if (!marketingStandard) {
      return undefined;
    }

    const normalized = marketingStandard.toLowerCase().replace(/[^a-z]/g, '');
    const mapping: Record<string, number> = {
      organic: 0,
      freerange: 1,
      cagefree: 2,
      caged: 3,
    };

    return mapping[normalized];
  }

  private getFirstDimensionValue(
    values?: Array<{ weekNumber?: number; dimensionValue?: number }>,
  ): number | undefined {
    const firstValue = values?.find(
      (value) => value.dimensionValue !== undefined || value.weekNumber !== undefined,
    );
    return firstValue?.dimensionValue ?? firstValue?.weekNumber;
  }

  private getLastDimensionValue(
    values?: Array<{ weekNumber?: number; dimensionValue?: number }>,
  ): number | undefined {
    const arr = values ?? [];
    const lastValue = arr[arr.length - 1];
    return lastValue?.dimensionValue ?? lastValue?.weekNumber;
  }
}
